// @ts-nocheck
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import CheckmarkCircle2Line from "@store/Library/Components/Toolkit/Icons/CheckmarkCircle2/CheckmarkCircle2Line";
import AlertBox, { AlertBoxPresentationProps, AlertBoxProps } from "@store/Library/Components/AlertBox";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import updateChangeRequestOrder from "@store/Framework/Store/Greif/Pages/OrderChange/Handler/UpdateChangeRequestOrder";
import React, { FC, useContext, useEffect } from "react";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import getOrderChangeRequestProbability from "@store/Framework/Common/Utilities/OrderChangeRequest/getOrderChangeRequestProbability";
import translate from "@insite/client-framework/Translate";
import siteMessage from "@insite/client-framework/SiteMessage";
import { css } from "styled-components";

export interface OrderChangeRequestProbabilityAlertProps {}

export interface OrderChangeRequestProbabilityAlertStyles {
    errorAlertBox?: AlertBoxPresentationProps;
    successAlertBox?: AlertBoxPresentationProps;
    warningAlertBox?: AlertBoxPresentationProps;
}

const sharedAlertBoxStyles: AlertBoxPresentationProps = {
    extendedStyles: {
        isCentered: true,
        tooltip: {
            mergeCss: true,
            cssOverrides: {
                tooltipContainer: css`
                    transform: translateX(-90%);
                `,
                tooltipArrow: css`
                    left: 90%;
                `,
            },
        },
    },
};

export const orderChangeRequestProbabilityAlertStyles: OrderChangeRequestProbabilityAlertStyles = {
    errorAlertBox: mergeProps<AlertBoxPresentationProps>(sharedAlertBoxStyles, {
        type: "error",
    }),
    successAlertBox: mergeProps<AlertBoxPresentationProps>(sharedAlertBoxStyles, {
        type: "success",
        extendedStyles: {
            icon: {
                src: CheckmarkCircle2Line,
            },
        },
    }),
    warningAlertBox: mergeProps<AlertBoxPresentationProps>(sharedAlertBoxStyles, {
        type: "warning",
    }),
};

const styles = orderChangeRequestProbabilityAlertStyles;

const getProbabilityProps = (probability: number | undefined): Partial<AlertBoxProps> | undefined => {
    switch (probability) {
        case 1:
            return {
                ...styles.errorAlertBox,
                title: translate("Change unlikely"),
                tooltipText: siteMessage("ChangeRequest_ChangeUnlikely").toString(),
            };
        case 2:
            return {
                ...styles.warningAlertBox,
                title: translate("Change conditional"),
                tooltipText: siteMessage("ChangeRequest_ChangeConditional").toString(),
            };
        case 3:
            return {
                ...styles.successAlertBox,
                title: translate("Change likely"),
                tooltipText: siteMessage("ChangeRequest_ChangeLikely").toString(),
            };
        default:
            return undefined;
    }
};

const OrderChangeRequestProbabilityAlert: FC<OrderChangeRequestProbabilityAlertProps> = () => {
    const dispatch = useAppDispatch();
    const { value: order } = useContext(OrderStateContext);
    const orderChangeRequest = useAppSelector(state => state.greif.pages.orderChange.changeRequestOrder);
    const { probability, newLines, orderLines, newRequestedDate } = orderChangeRequest;

    useEffect(() => {
        if (order) {
            const newProbability = getOrderChangeRequestProbability(orderChangeRequest, order);

            if (probability !== newProbability) {
                dispatch(updateChangeRequestOrder({ probability: newProbability }));
            }
        }
    }, [order, newLines, orderLines, newRequestedDate]);

    const probabilityProps = getProbabilityProps(probability);

    if (!probabilityProps) {
        return null;
    }

    return <AlertBox secondaryText={translate("(Pending confirmation)")} {...probabilityProps} />;
};

export default OrderChangeRequestProbabilityAlert;
